import React, { useState, useEffect } from 'react';
import {X, ChevronLeft, ChevronRight, Award, Gift, Zap, User, Users, Cpu, ExternalLink} from 'lucide-react';
import Nt from './Nt';

const UpgradeAnalysisModal = ({ isOpen, onClose, upgrades }) => {
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        const handleKeyPress = (e) => {
            if (!isOpen) return;

            if (e.key === 'ArrowLeft' && currentSlide > 0) {
                setCurrentSlide(prev => prev - 1);
            }
            if (e.key === 'ArrowRight' && currentSlide < upgrades.length - 1) {
                setCurrentSlide(prev => prev + 1);
            }
            if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [isOpen, currentSlide, upgrades, onClose]);

    useEffect(() => {
        if (isOpen) {
            setCurrentSlide(0);
        }
    }, [isOpen]);

    if (!isOpen || !upgrades || upgrades.length === 0) return null;

    const currentUpgrade = upgrades[currentSlide];

    const nextSlide = () => {
        if (currentSlide < upgrades.length - 1) {
            setCurrentSlide(prev => prev + 1);
        }
    };

    const prevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(prev => prev - 1);
        }
    };

    const getCloneTypeIcon = (type) => {
        switch (type) {
            case 'technical':
                return <Cpu size={18} className="text-gray-500" />;
            case 'personal':
                return <Users size={18} className="text-blue-500" />;
            default:
                return <User size={18} className="text-green-500" />;
        }
    };

    const getCloneTypeName = (type) => {
        switch (type) {
            case 'technical':
                return 'Технический';
            case 'personal':
                return 'Личный клон';
            default:
                return 'Аккаунт';
        }
    };

    // Получаем данные позиции из апгрейда
    const position = currentUpgrade?.next_position;
    const nextClosure = position?.next_closure;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#2b6c8e] to-[#a7d7cd] p-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
                    <div>
                        <h2 className="text-xl font-bold text-white">
                            ⚡ Апгрейд {currentSlide + 1} из {upgrades.length}
                        </h2>
                        <p className="text-white text-sm opacity-90">
                            {currentUpgrade?.target_tarif} → Уровень {currentUpgrade?.target_level}$
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Информация об апгрейде */}
                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-4 border-2 border-amber-300 mb-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Zap size={20} className="text-amber-600" />
                            <span className="font-bold text-amber-800 text-lg">Апгрейд активирован!</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                                <span className="text-gray-600">Целевой тариф:</span>
                                <div className="font-semibold text-gray-800">{currentUpgrade?.target_tarif}</div>
                            </div>
                            <div>
                                <span className="text-gray-600">Уровень:</span>
                                <div className="font-semibold text-gray-800">{currentUpgrade?.target_level}$</div>
                            </div>
                            {currentUpgrade?.sponsor_login && (
                                <>
                                    <div>
                                        <span className="text-gray-600">Спонсор:</span>
                                        <div className="font-semibold text-gray-800"><Nt>{currentUpgrade.sponsor_login}</Nt></div>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">ID спонсора:</span>
                                        <div className="font-semibold text-gray-800">{currentUpgrade.sponsor_id}</div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Позиция после апгрейда */}
                    {position && (
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border-2 border-blue-300 mb-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    {getCloneTypeIcon(position.clone_type)}
                                    <div>
                                        <div className="font-bold text-gray-800">
                                            Позиция #{position.position_id}
                                        </div>
                                        <a
                                            href={`https://light-platform.com/dashboard/clusters/lite-box?program=${position.program}&tarif=${position.tarif}&level=${position.level}&mentor=${position.position_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition flex items-center gap-2 w-auto"
                                            title="Перейти к позиции апгрейда"
                                            style={{width: '50px'}}>
                                            <ExternalLink size={20}/>
                                        </a>
                                        <div className="text-sm text-gray-600">
                                            <Nt>{position.login}</Nt> ({getCloneTypeName(position.clone_type)})
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xl font-bold text-gray-800">
                                        {position.closed}/{position.total_positions}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {position.created_at}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Следующее закрытие в этой позиции */}
                    {nextClosure && (
                        <div className="bg-white rounded-lg border-2 border-gray-200">
                            <div className="bg-gray-50 px-4 py-2 border-b-2 border-gray-200">
                                <div className="font-semibold text-gray-700 flex items-center justify-between">
                                    <span>📊 Следующее закрытие: Позиция {nextClosure.position_number}</span>
                                    <span className="text-green-600">Всего: ${nextClosure.total_rewards || 0}</span>
                                </div>
                            </div>

                            <div className="p-4 space-y-3">
                                {/* Вознаграждения */}
                                {nextClosure.rewards && nextClosure.rewards.length > 0 && (
                                    <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                                        <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                                            <Award size={16} className="text-yellow-600" />
                                            Прямые вознаграждения:
                                        </div>
                                        <div className="space-y-1">
                                            {nextClosure.rewards.map((reward, idx) => (
                                                <div key={idx} className="flex items-center justify-between text-sm bg-white rounded px-3 py-1.5">
                          <span className="text-gray-700">
                            {reward.recipient_login} <span className="text-gray-500">({reward.type})</span>
                          </span>
                                                    <span className="font-semibold text-green-600">
                            +${reward.amount}
                          </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Реферальные */}
                                {nextClosure.referral_rewards && nextClosure.referral_rewards.length > 0 && (
                                    <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                                        <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                                            <Users size={16} className="text-purple-600" />
                                            Реферальные вознаграждения:
                                        </div>
                                        <div className="space-y-1">
                                            {nextClosure.referral_rewards.map((reward, idx) => (
                                                <div key={idx} className="flex items-center justify-between text-sm bg-white rounded px-3 py-1.5">
                          <span className="text-gray-700">
                            {reward.recipient_login} <span className="text-gray-500">(уровень {reward.level})</span>
                          </span>
                                                    <span className="font-semibold text-purple-600">
                            +${reward.amount}
                          </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Клоны */}
                                {nextClosure.clones && nextClosure.clones.length > 0 && (
                                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                                        <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                                            <Gift size={16} className="text-blue-600" />
                                            Начисление клонов:
                                        </div>
                                        <div className="space-y-1">
                                            {nextClosure.clones.map((clone, idx) => (
                                                <div key={idx} className="flex items-center justify-between text-sm bg-white rounded px-3 py-1.5">
                          <span className="text-gray-700">
                            {clone.type === 'technical' ? 'Технические клоны' : 'Обычные клоны'}
                          </span>
                                                    <span className="font-semibold text-blue-600">
                            +{clone.count} шт
                          </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Следующий апгрейд */}
                                {nextClosure.upgrade && (
                                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-3 border-2 border-orange-300">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Zap size={16} className="text-orange-600" />
                                            <span className="font-bold text-orange-800 text-sm">
                        {currentSlide < upgrades.length - 1 ?
                            '→ Есть следующий апгрейд (листайте вправо)' :
                            '→ Последний апгрейд в цепочке'
                        }
                      </span>
                                        </div>
                                        <div className="text-xs text-gray-700">
                                            Следующий апгрейд: {nextClosure.upgrade.target_tarif} → {nextClosure.upgrade.target_level}$
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Если позиции нет */}
                    {!position && (
                        <div className="bg-gray-50 rounded-lg p-6 text-center">
                            <div className="text-gray-500 mb-2">ℹ️</div>
                            <p className="text-gray-600">
                                Информация о позиции после апгрейда недоступна
                            </p>
                        </div>
                    )}
                </div>

                {/* Navigation */}
                <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t flex-shrink-0 rounded-b-2xl">
                    <button
                        onClick={prevSlide}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                            currentSlide === 0
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-[#2b6c8e] hover:bg-[#d5ebe6]'
                        }`}
                        disabled={currentSlide === 0}
                    >
                        <ChevronLeft size={20} />
                        <span className="hidden sm:inline">Предыдущий</span>
                    </button>

                    <div className="flex items-center gap-2">
                        {upgrades.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                className={`transition-all rounded-full ${
                                    index === currentSlide
                                        ? 'bg-[#2b6c8e] w-8 h-3'
                                        : 'bg-gray-300 hover:bg-gray-400 w-3 h-3'
                                }`}
                                title={`Апгрейд ${index + 1}`}
                            />
                        ))}
                    </div>

                    <button
                        onClick={nextSlide}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                            currentSlide === upgrades.length - 1
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-[#2b6c8e] hover:bg-[#d5ebe6]'
                        }`}
                        disabled={currentSlide === upgrades.length - 1}
                    >
                        <span className="hidden sm:inline">Следующий</span>
                        <ChevronRight size={20} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UpgradeAnalysisModal;
